import {Subscription} from "rxjs";
import {FormsModule} from "@angular/forms";
import {RouterLink} from "@angular/router";
import {Component, inject, OnDestroy, OnInit} from '@angular/core';

import {appRoutes} from "@numizmat/constants";
import {environment} from "@numizmat/environments";
import {LoginModalComponent} from "@numizmat/modals";
import {CurrentUserStateService} from "@numizmat/services/account";
import {PopularAdCategoryResponse} from "@numizmat/services-models/core";
import {AdCardComponent, AdCardData, LogoComponent} from "@numizmat/components";
import {JsonLdService, MetaTagService, ModalService} from "@numizmat/services/common";
import {PopularAdApiService, PopularAdCategoryApiService} from "@numizmat/services/core";
import {EventsSectionComponent} from "@numizmat/pages/home-page/components/events-section/events-section.component";
import {
  CategoriesSectionComponent
} from "@numizmat/pages/home-page/components/categories-section/categories-section.component";

@Component({
    selector: 'nz-home-page',
    imports: [
        RouterLink,
        FormsModule,
        AdCardComponent,
        EventsSectionComponent,
        CategoriesSectionComponent,
        LogoComponent
    ],
    templateUrl: './home-page.component.html',
    styleUrl: './home-page.component.scss'
})
export class HomePageComponent implements OnInit, OnDestroy {
  private modalService: ModalService = inject(ModalService);
  private jsonLdService: JsonLdService = inject(JsonLdService);
  private metaTagService: MetaTagService = inject(MetaTagService);
  private popularAdApiService: PopularAdApiService = inject(PopularAdApiService);
  private currentUserStateService: CurrentUserStateService = inject(CurrentUserStateService);
  private popularAdCategoryApiService: PopularAdCategoryApiService = inject(PopularAdCategoryApiService);

  private subs: Subscription = new Subscription();

  protected searchValue?: string;
  protected userLoggedIn: boolean = false;
  protected popularAds: AdCardData[] = [];
  protected popularAdCategories: PopularAdCategoryResponse[] = [];

  protected readonly appRoutes = appRoutes;

  public ngOnInit(): void {
    this.getCurrentUser();
    this.getLoginState();
    this.getAllPopularAds();
    this.getAllPopularAdCategories();
    this.setMetaTags();
  }

  public ngOnDestroy(): void {
    this.subs.unsubscribe();
    this.jsonLdService.removeSchema();
  }

  protected onOpenLoginModal(): void {
    if (!this.userLoggedIn) {
      this.modalService.open(LoginModalComponent, {
        scrollable: true
      });
    }
  }

  private getCurrentUser(): void {
    const subscription: Subscription = this.currentUserStateService.getCurrentUser().subscribe({
      next: currentUser => {
        this.userLoggedIn = currentUser.loggedIn;
      }
    });
    this.subs.add(subscription);
  }

  private getLoginState(): void {
    const subscription: Subscription = this.currentUserStateService.getLoginState().subscribe({
      next: loginState => {
        if (loginState) {
          this.getAllPopularAds();
        }
      }
    });
    this.subs.add(subscription);
  }

  private getAllPopularAds(): void {
    this.popularAdApiService.getAllPopularAds().subscribe({
      next: response => {
        this.popularAds = response.map((x, index) => {
          return {
            adId: x.adId,
            title: x.title,
            imageUrl: `${environment.apiImageAdUrl}/main/${x.adId}`,
            imagePriority: index <= 3,
            price: x.price,
            adType: x.adType,
            expiryDate: x.endDateTime,
            userAdLikeId: x.userAdLikeId,
            additionalImageIds: x.additionalImageIds
          }
        });
        this.setSchema();
      }
    });
  }

  private getAllPopularAdCategories(): void {
    this.popularAdCategoryApiService.getAllPopularAdCategories().subscribe({
      next: response => {
        this.popularAdCategories = response;
        this.setSchema();
      }
    });
  }

  private setSchema(): void {
    if (this.popularAds.length === 0 || this.popularAdCategories.length === 0) {
      return;
    }
    const adList = this.popularAds.map(ad => ({
      "@type": "Product",
      "name": ad.title,
      "image": [ad.imageUrl],
      "description": ad.title,
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": 5,
        "bestRating": 5,
        "ratingCount": 89
      },
      "offers": {
        "@type": "Offer",
        "price": ad.price,
        "priceCurrency": "BGN",
        "url": `${environment.applicationDomain}/ad/${ad.adId}`,
        "availability": "https://schema.org/InStock",
        "hasMerchantReturnPolicy": {
          "@type": "MerchantReturnPolicy",
          "name": "Връщане не е разрешено",
          "applicableCountry": "BG",
          "returnPolicyCategory": "https://schema.org/MerchantReturnNotPermitted"
        },
        "shippingDetails": {
          "@type": "OfferShippingDetails",
          "shippingDestination": {
            "@type": "DefinedRegion",
            "addressCountry": "BG"
          },
          "shippingRate": {
            "@type": "MonetaryAmount",
            "currency": "BGN",
            "value": 0
          },
          "deliveryTime": {
            "@type": "ShippingDeliveryTime",
            "handlingTime": {
              "@type": "QuantitativeValue",
              "minValue": 2,
              "maxValue": 5,
              "unitCode": "DAY"
            },
            "transitTime": {
              "@type": "QuantitativeValue",
              "minValue": 2,
              "maxValue": 5,
              "unitCode": "DAY"
            }
          },
          "description": "Съгласно общите условия на сайта и условията посочени от продавача."
        },
        "priceValidUntil": ad.expiryDate
      }
    }));
    const categoryList = this.popularAdCategories.map(category => ({
      "@type": "CategoryCode",
      "name": category.adCategoryName,
      "description": category.adCategoryDescription,
      "url": `${environment.applicationDomain}/ad-list?adCategoryId=${category.adCategoryId}`,
      "codeValue": category.adCategoryId
    }));
    const homePageSchema = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "Numizmat",
      "url": `${environment.applicationDomain}/`,
      "potentialAction": {
        "@type": "SearchAction",
        "target": `${environment.applicationDomain}/ad-list?searchValue={search_term_string}`,
        "query-input": "required name=search_term_string"
      },
      "description": "Място за колекционери на монети, банкноти, пощенски марки и други редки предмети.",
      "hasPart": [
        {
          "@type": "CollectionPage",
          "name": "Популярни обяви",
          "url": `${environment.applicationDomain}/`,
          "isPartOf": `${environment.applicationDomain}/`,
          "mainEntity": adList
        },
        {
          "@type": "ItemList",
          "name": "Основни категории",
          "itemListElement": categoryList
        }
      ]
    };
    this.jsonLdService.setSchema(homePageSchema);
  }

  private setMetaTags(): void {
    this.metaTagService.setCanonicalUrl(`${environment.applicationDomain}`);
    this.metaTagService.setTitle('Онлайн пазар за колекционери. Монети, банкноти, пощенски марки и други колекционерски артикули | Collectoria');
    this.metaTagService.setDescription('Онлайн пазар за колекционери. Монети, банкноти, пощенски марки и други колекционерски артикули | Collectoria');
  }
}
